import { Component, DoCheck, Input, ViewChild } from '@angular/core';
import { EditProductPlanAutoRenewalComponent } from 'components/accounting/product-plan/edit-product-plan-auto-renewal.component';
import { TerminateProductPlanComponent } from 'components/accounting/product-plan/terminate-product-plan.component';
import { UndoTerminateProductPlanComponent } from 'components/accounting/product-plan/undo-terminate-product-plan.component';
import { ButtonComponent } from 'components/input/button/button.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { CredentialsDirective } from 'directives/credential.directive';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { Layout } from 'modules/layout';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { PaymentIntervalPipe } from 'pipes/payment-interval.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ReasonService } from 'services/reason.service';
import { BrandService } from 'services/brand.service';
import { BrandModel } from 'models/generated/Entities.GormCrm.Brand';

@Component({
	selector: 'accounting-plan',
	templateUrl: './accounting-plan.component.html',
	providers: [FormValidationService],
	imports: [CredentialsDirective, Layout, CurrencyPipe, TranslatePipe, CustomDatePipe, PaymentIntervalPipe]
})
export class AccountingPlanComponent implements DoCheck {

	@ViewChild('registerStartButton') registerStartButton!: ButtonComponent;

	editAccountingCredential = CredentialType.EditAccountingPlan;
	startProductPlanManuallyAllowed = CredentialType.StartProductPlanManually;
	productPlanTerminationDate: Date | null = null;
	productPlanAnnualValueForRenewal: number = 0;
	maxCounterValue: number | undefined;
	state: string | null = null;
	inputType = InputType;
	minDate = new Date();
	terminationReason?: ReasonModel;
	noTerminationReasonLabel?: string;
	brand?: BrandModel;

	@Input() accountingPlans!: AccountingPlanModel[];
	private _productPlan!: ProductPlanModel;
	@Input()
	set productPlan(plan: ProductPlanModel) {
		this._productPlan = plan;
		this.brand = this.brandService.getBrand(plan.brandId) ?? undefined;
		if (plan.terminationDate != undefined) this.productPlanTerminationDate = plan.terminationDate;
		this.productPlanAnnualValueForRenewal = plan.annualValueForRenewal;
		this.loadTerminationReason();
		this.loadStartPlanRestrictions();
	}
	get productPlan() {
		return this._productPlan;
	}

	private _accountingPlan!: AccountingPlanModel;
	@Input()
	set accountingPlan(plan: AccountingPlanModel) {
		this._accountingPlan = plan;
		this.getMaxCounterValue();
	}
	get accountingPlan() {
		return this._accountingPlan;
	}

	private startPlanRestriction?: Promise<string | null>;
	private dataProvider: CompanyDataProvider;

	constructor(
		dataProviderFactory: CompanyDataProviderFactory,
		private readonly navigation: NavigationService,
		private readonly accountingService: AccountingService,
		private readonly notificationService: NotificationService,
		private readonly dateService: DateService,
		private readonly brandService: BrandService, 
		private readonly reasonService: ReasonService) {
		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();
	}

	editAutoRenewal() {
		this.navigation.openPopup(EditProductPlanAutoRenewalComponent, 'company.product-plans.context-menu.auto-revewal.pop-up-title').then(view => view.productPlan = this.productPlan)
	}

	getMaxCounterValue() {
		this.maxCounterValue = undefined;
		this.accountingService.getMaxCounterValue(this.accountingPlan.id!).then((result) => {
			this.maxCounterValue = result;
		});
	}

	setState(state: string) {
		this.state = this.state === state ? null : state;
	}

	private terminationReasonId?: string;
	ngDoCheck(): void {
		if (this.productPlan?.terminationReasonId != this.terminationReasonId) this.loadTerminationReason();
	}
	async loadTerminationReason() {
		this.terminationReasonId = this.productPlan.terminationReasonId;
		if (this.terminationReasonId) {
			this.terminationReason = await this.reasonService.getReason(this.terminationReasonId);
		}
		else this.terminationReason = undefined;

		this.noTerminationReasonLabel = await this.dataProvider.planIsReplaced(this.productPlan) ? 'reason.upgraded' : 'reason.unknown';
	}

	loadStartPlanRestrictions() {
		if (![ProductPlanTagConstants.domainDk, ProductPlanTagConstants.domainForeign].some(domainTag => this.productPlan.productPlanTags.map(t => t.tag).indexOf(domainTag) >= 0))
			return this.startPlanRestriction = Promise.resolve(null);
			
		return this.startPlanRestriction = this.dataProvider.getDomains().then(domains => {
			if (!domains.some(domain => domain.productPlanId == this.productPlan.id)) return 'missing-domain';
			return null;
		});
	}


	allowUndoTerminate(): boolean {
		// It's too late to undo termination only if enddate is yesterday or earlier, since the plan would not get autorenewed
		return this.productPlan?.terminationDate != undefined && this.productPlan?.autoRenewalDate! >= this.dateService.today();
	}

	undoTerminate() {
		if (!this.productPlan) return;

		if (this.productPlan.restrictEndDate) {

			this.navigation.openPopup(UndoTerminateProductPlanComponent, 'company.product-plans.undo-terminate.popup-header').then(view => {
				view.productPlan = this.productPlan;
				view.accountingPlanId = this.accountingPlan.id
			});

		} else {

			this.notificationService.confirm('company.accounting-plans.undo-terminate.confirm-message').subscribe(result => {
				if (result) this.accountingService.undoTerminateAccountingPlan(this.accountingPlan.id!, undefined);
			});
		}
	}

	initiateTerminate() {

		this.navigation.openPopup(TerminateProductPlanComponent, 'company.product-plans.terminate.popup-header').then(view => {
			view.accountingPlan = this.accountingService.filterActiveAccountingPlan(this.accountingPlans);
			view.productPlan = this.productPlan;
		});
	}

	async registerStartOfPlan() {
		if (this.accountingPlan.startDate || !this.startPlanRestriction) return;

		const restriction = await this.startPlanRestriction;
		if (restriction) {
			this.notificationService.warning(`company.accounting-plans.cannot-start-plan.${restriction}`);
			this.registerStartButton.reset();
			return;
		}

		await this.accountingService.registerStartOfPlan(this.accountingPlan.id!);
		this.registerStartButton.succeeded();
	}
}
